import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  InputSignal,
  model,
  OnDestroy,
  OnInit,
  output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { MzicDurationPipe } from '@mzic/mzic-utils';
import { NgxMaskDirective } from 'ngx-mask';
import { debounceTime, Subject } from 'rxjs';
import { AudioCacheService } from '../../services/audio-cache.service';
import { MzicSvgComponent } from '../mzic-svg/mzic-svg.component';
import { MzicToggleComponent } from '../mzic-toggle/mzic-toggle.component';
import { PreviewIntervalTime } from './mzic-audio-player.model';
import {
  MzicAudioPlayerCtrl,
  MzicAudioPlayerFile,
} from './mzic-audio-player.type';
import { getAudioData, linearPath } from './waveform-path';

@Component({
  selector: 'mzic-audio-player',
  imports: [
    CommonModule,
    MzicToggleComponent,
    MzicSvgComponent,
    ReactiveFormsModule,
    NgxMaskDirective,
    TranslocoModule,
    MzicDurationPipe,
  ],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-audio-player',
      alias: 'player',
    }),
  ],
  templateUrl: './mzic-audio-player.component.html',
  styleUrls: ['./mzic-audio-player.component.scss'],
})
export class MzicAudioPlayerComponent implements OnInit, OnDestroy {
  @ViewChild('audioPlayer') audioPlayer!: ElementRef<HTMLAudioElement>;
  @ViewChild('svgContainer') svgContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('audioSvg') audioSvg!: ElementRef<SVGElement>;
  @ViewChild('svgPath') svgPath!: ElementRef<SVGPathElement>;

  width = model(0);
  type = model('mirror');
  samples = model(150);
  height = input(100);
  colorDefault = input('#545454');
  colorActive = input('#9655ff');
  paths = input([{ d: 'V', sy: 0, x: 50, ey: 100 }]);
  showAudioPlayer = input(false);
  showInputFile = input(false);
  showPreviewButton = input(false);
  showDuration = input(false);
  previewDuration = input(15);
  showStartPauseButton = input(false);
  trackId = input<string>();
  ctrl: InputSignal<Subject<MzicAudioPlayerCtrl> | undefined> = input();

  mzicError = output<ErrorEvent>();
  mzicPlay = output<Event>();
  mzicPlaying = output<Event>();
  mzicPause = output<Event>();
  mzicTimeUpdate = output<Event>();
  mzicCanPlay = output<Event>();
  mzicLoadedMetaData = output<Event>();
  mzicLoadStart = output<Event>();
  mzicEnded = output<Event>();
  mzicLoadedData = output<Event>();
  mzicPreviewInterval = output<PreviewIntervalTime>();
  mzicAudioDataLoaded = output<{
    audioBuffer: AudioBuffer | null;
    duration: number;
    trackId: string;
  }>();

  protected startTimeControl = new FormControl<string | null>('0');

  private _destroyRef = inject(DestroyRef);
  private audioCacheService = inject(AudioCacheService);
  private windowResize = new Subject<void>();
  private drawWaveTimeout?: ReturnType<typeof setTimeout>;
  private svgPathTimeout?: ReturnType<typeof setTimeout>;
  private _isDestroyed = false;

  // Cache-related properties
  private currentTrackId = '';
  private lastGeneratedWidth = 0;
  private isProcessingWaveform = false;

  fileName = '';
  audioPercent = 0;
  audioPath = '';
  audioData: any;
  loading = false;
  drawPath = '';

  xPosition = 0;
  svgContainerWidth = 0;
  duration = 0;

  showPreview = false;
  audioIsPlaying = false;
  startPlayInsidePreview!: number;
  startPlayTimeInsidePreview!: number;

  @HostListener('window:resize', ['$event.target'])
  public onResize() {
    this.windowResize.next();
  }

  async ngOnInit() {
    this.startTimeControl.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => {
        if (value) this.setPreviewStartTime(value);
      });

    if (this.ctrl()) {
      this.ctrl()
        ?.pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe((action) => this.manageCtrlAction(action));
    }

    this.windowResize
      .asObservable()
      .pipe(debounceTime(300), takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this.windowResized());

    const element = document.getElementById('previewContainer');
    if (element) {
      this.dragElement(element);
    }
  }

  dragElement(element: HTMLElement) {
    let pos1 = 0;
    // let pos2 = 0;
    let pos3 = 0;
    // let pos4 = 0;

    const closeDragElement = () => {
      document.onmouseup = null;
      document.onmousemove = null;

      this.setPreviewStartTime();
    };

    const dragMouseDown = (e: any) => {
      e = e || window.event;
      e.preventDefault();
      pos3 = e.clientX;
      // pos4 = e.clientY;
      document.onmouseup = closeDragElement;
      document.onmousemove = elementDrag;
    };

    const elementDrag = (e: any) => {
      e = e || window.event;
      e.preventDefault();

      // calculate the new cursor position:
      pos1 = pos3 - e.clientX;
      // pos2 = pos4 - e.clientY;
      pos3 = e.clientX;
      // pos4 = e.clientY;
      // set the element's new position:
      // element.style.top = element.offsetTop - pos2 + 'px';

      this.xPosition = element.offsetLeft - pos1;
      if (this.xPosition < 0) {
        this.xPosition = 0;
      }

      const elementRect = element.getBoundingClientRect();
      const elementWidth = elementRect.width;

      if (this.xPosition + elementWidth >= this.svgContainerWidth) {
        this.xPosition = this.svgContainerWidth - elementWidth;
      }

      const durationPosition = this.pxToTime(this.xPosition);
      this.setTime(durationPosition);
      element.style.left = this.xPosition + 'px';
    };

    element.onmousedown = dragMouseDown;
  }

  ngOnDestroy() {
    this._isDestroyed = true;
    this.isProcessingWaveform = false;

    // Clear any pending timeouts to prevent operations after component destruction
    if (this.drawWaveTimeout) {
      clearTimeout(this.drawWaveTimeout);
    }
    if (this.svgPathTimeout) {
      clearTimeout(this.svgPathTimeout);
    }

    // Reset component state
    this.loading = false;
    this.audioData = null;
    this.drawPath = '';
  }

  setPreviewStartTime(time?: string) {
    if (this._isDestroyed) {
      return;
    }

    const element = document.getElementById('previewContainer') as HTMLElement;
    let previewTime = 0;
    if (time) {
      previewTime = parseInt(time);
    } else {
      previewTime = parseInt(this.pxToTime(this.xPosition).toFixed(2), 10);
      this.startTimeControl.setValue(previewTime.toString().padStart(4, '0'));
    }

    element.style.left = this.timeToPx(previewTime) + 'px';
    const duration = this.audioPlayer?.nativeElement?.duration;
    const tempEndTime = previewTime + this.previewDuration();
    const endTime = tempEndTime < duration ? tempEndTime : duration;

    this.startPlayTimeInsidePreview = previewTime;
    this.startPlayInsidePreview = previewTime / duration;
    this.mzicPreviewInterval.emit({ startTime: previewTime, endTime: endTime });
  }

  windowResized() {
    if (this._isDestroyed || !this.svgContainer?.nativeElement) {
      return;
    }

    const newWidth =
      this.svgContainer.nativeElement.getBoundingClientRect().width;
    const widthDifference = Math.abs(newWidth - this.lastGeneratedWidth);

    // Only regenerate waveform if width has changed significantly (more than 10px)
    if (widthDifference > 10) {
      this.drawWave();
    }
  }

  setComponentsProps() {
    if (this.svgContainer?.nativeElement && this.duration) {
      const svgContainerRect =
        this.svgContainer.nativeElement.getBoundingClientRect();
      this.svgContainerWidth = svgContainerRect.width || 0;
    }
  }

  buttonPlay() {
    if (!this.audioPath) {
      console.warn('🎵 Cannot play - no audio path set');
      return;
    }
    if (this.showPreview) this.setTime(this.startPlayTimeInsidePreview);
    this.ctrl()?.next({ action: 'play' });
  }

  buttonPause() {
    if (!this.audioPath) {
      console.warn('🎵 Cannot pause - no audio path set');
      return;
    }
    this.ctrl()?.next({ action: 'pause' });
  }

  enablePreview(enable: boolean) {
    this.showPreview = enable;
  }

  setPreviewStart(time: string) {
    const t = time.split(':');
    const minutes = parseInt(t[0]);
    const seconds = parseInt(t[1]);
    let timeSeconds = minutes * 60 + seconds;

    if (timeSeconds >= this.duration - this.previewDuration()) {
      timeSeconds = this.duration - this.previewDuration();
    }

    this.setTime(timeSeconds);
    this.movePreviewStart(timeSeconds);
  }

  pxToTime(px: number) {
    return (px * this.duration) / this.svgContainerWidth;
  }

  timeToPx(duration: number) {
    return (duration * this.svgContainerWidth) / this.duration;
  }

  movePreviewStart(duration: number) {
    const position = this.timeToPx(duration);
    const element = document.getElementById('previewContainer');
    if (element) {
      element.style.left = position + 'px';
    }
  }

  setTime(duration: number) {
    if (this.audioPlayer?.nativeElement) {
      this.audioPlayer.nativeElement.currentTime = duration;
    }
  }

  setAudioPercent(percent: number) {
    this.audioPercent = percent;
  }

  setFile(file: MzicAudioPlayerFile) {
    // Validate file path
    if (!file?.path) {
      console.error('Invalid file path provided:', file);
      this.loading = false;
      return;
    }

    // Reset loading state and clear existing data
    this.loading = true;
    this.isProcessingWaveform = false;
    this.drawPath = '';
    this.audioData = null;

    // Set new audio path and filename
    this.audioPath = file.path;
    this.fileName = file.name;

    // Generate new track ID for caching
    this.currentTrackId = this.generateTrackIdFromPath(this.audioPath);

    // Clear any existing timeouts
    if (this.drawWaveTimeout) {
      clearTimeout(this.drawWaveTimeout);
    }
    if (this.svgPathTimeout) {
      clearTimeout(this.svgPathTimeout);
    }

    // Start waveform generation immediately to show wave faster
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      this.drawWave();
    });
  }

  manageCtrlAction(ctrl: MzicAudioPlayerCtrl) {
    switch (ctrl.action) {
      case 'setFile':
        if (ctrl.file) {
          this.setFile(ctrl.file);
        }
        break;

      case 'setPercent':
        this.setAudioPercent(ctrl.data as number);
        break;

      case 'setPreviewStart':
        this.setPreviewStart(ctrl.data as string);
        break;

      case 'enablePreview':
        this.enablePreview(ctrl.data as boolean);
        break;

      case 'play':
        this.play();
        break;

      case 'pause':
        this.pause();
        break;
    }
  }

  async drawWave() {
    if (this._isDestroyed || this.isProcessingWaveform || !this.audioPath) {
      return;
    }

    // Clear any existing timeouts before starting new processing
    if (this.drawWaveTimeout) {
      clearTimeout(this.drawWaveTimeout);
    }
    if (this.svgPathTimeout) {
      clearTimeout(this.svgPathTimeout);
    }

    this.isProcessingWaveform = true;
    this.loading = true;

    try {
      // Generate track ID from audio path for caching
      this.currentTrackId = this.generateTrackIdFromPath(this.audioPath);
      console.log(
        `🎵 Generated trackId: ${this.currentTrackId} from audioPath: ${this.audioPath.substring(0, 100)}...`,
      );

      // Get current container width with fallback
      let containerWidth =
        this.svgContainer?.nativeElement?.getBoundingClientRect().width || 0;

      // If width is still 0, wait a bit and try again
      if (containerWidth <= 0) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        containerWidth =
          this.svgContainer?.nativeElement?.getBoundingClientRect().width ||
          800;
      }

      // Check memory cache for existing waveform
      const cachedEntry = this.audioCacheService.getCachedWaveform(
        this.currentTrackId,
        this.audioPath,
        containerWidth,
      );

      if (cachedEntry?.waveformPath) {
        // We have a cached waveform path, determine cache type properly
        const hasAudioBuffer =
          cachedEntry.audioBuffer && cachedEntry.audioBuffer.length > 0;

        if (hasAudioBuffer) {
          // True memory cache - instant load with AudioBuffer
          console.log(
            '🎵 Using MEMORY cache (with AudioBuffer) - truly instant!',
          );
          this.applyWaveformFromCache(cachedEntry, containerWidth);
        } else {
          // Restored from storage or storage cache - fast visual load
          console.log(
            '🎵 Using STORAGE cache (waveform path only) - fast visual, generating AudioBuffer in background',
          );
          this.applyWaveformFromCacheWithoutAudioBuffer(
            cachedEntry,
            containerWidth,
          );
        }
        return;
      }

      // Generate fresh waveform
      await this.generateFreshWaveform(containerWidth);
    } catch (error) {
      console.error('Error in drawWave:', error);
      this.loading = false;
      this.isProcessingWaveform = false;
      this.mzicError.emit(error as ErrorEvent);
    }
  }

  private applyWaveformFromCache(
    cachedEntry: any,
    containerWidth: number,
  ): void {
    if (this._isDestroyed) {
      return;
    }

    console.log('🎵 Applying cached waveform (memory cache - instant!)');

    this.audioData = cachedEntry.audioBuffer;
    this.duration = cachedEntry.duration;
    this.drawPath = cachedEntry.waveformPath;
    this.width.set(containerWidth);
    this.lastGeneratedWidth = containerWidth;

    // For memory cache hits, apply immediately - no setTimeout needed
    if (this.svgPath?.nativeElement) {
      this.svgPath.nativeElement.setAttribute('d', this.drawPath);
    } else {
      // Fallback to querySelector if ViewChild not ready
      const pathElement = document.querySelector(
        '#audioSvg path',
      ) as SVGPathElement;
      if (pathElement) {
        pathElement.setAttribute('d', this.drawPath);
      }
    }

    // Complete all states immediately for instant loading
    this.loading = false;
    this.isProcessingWaveform = false;
    this.setComponentsProps();
    this.setPreviewWidth();

    // Emit completion event immediately - include cache flag information
    this.mzicAudioDataLoaded.emit({
      audioBuffer: this.audioData,
      duration: this.duration,
      trackId: this.currentTrackId,
    });

    console.log('🎵 Memory cache applied instantly!');
  }

  private applyWaveformFromCacheWithoutAudioBuffer(
    cachedEntry: any,
    containerWidth: number,
  ): void {
    if (this._isDestroyed) {
      return;
    }

    console.log(
      '🎵 Applying cached waveform path (storage cache - fast visual load!)',
    );

    // Apply cached waveform path immediately for instant visual feedback
    this.drawPath = cachedEntry.waveformPath;
    this.duration = cachedEntry.duration;
    this.width.set(containerWidth);
    this.lastGeneratedWidth = containerWidth;

    // Apply the cached path immediately for instant visual feedback
    if (this.svgPath?.nativeElement) {
      this.svgPath.nativeElement.setAttribute('d', this.drawPath);
    } else {
      // Fallback to querySelector if ViewChild not ready
      const pathElement = document.querySelector(
        '#audioSvg path',
      ) as SVGPathElement;
      if (pathElement) {
        pathElement.setAttribute('d', this.drawPath);
      }
    }

    // CRITICAL: Clear loading states immediately so waveform appears instantly
    this.loading = false;
    this.isProcessingWaveform = false;

    // Set components props immediately
    this.setComponentsProps();
    this.setPreviewWidth();

    // Emit completion event immediately so parent component stops loading spinner
    this.mzicAudioDataLoaded.emit({
      audioBuffer: null, // Will be updated when background generation completes
      duration: this.duration,
      trackId: this.currentTrackId,
    });

    console.log('🎵 Cached waveform applied instantly - user can see it now!');

    // Generate AudioBuffer in background for playback functionality (doesn't affect visual)
    this.generateAudioBufferInBackground(cachedEntry, containerWidth);
  }

  private async generateAudioBufferInBackground(
    cachedEntry: any,
    containerWidth: number,
  ): Promise<void> {
    // For storage cache hits, emit completion immediately so spinner stops
    // User can see the waveform instantly while AudioBuffer generates in background
    this.loading = false;
    this.isProcessingWaveform = false;

    // Emit completion event immediately with temporary AudioBuffer
    this.mzicAudioDataLoaded.emit({
      audioBuffer: null as any, // Temporary - will be updated when real AudioBuffer is ready
      duration: this.duration,
      trackId: this.currentTrackId,
    });

    console.log(
      '🎵 Storage cache applied instantly, generating AudioBuffer in background...',
    );

    try {
      // Generate AudioBuffer for playback functionality in background
      this.audioData = await getAudioData(this.audioPath);
      if (!this.audioData) {
        throw new Error('Failed to load audio data for cached waveform');
      }

      // Update cache with AudioBuffer for next time (upgrade to memory cache)
      const updatedCacheEntry = {
        ...cachedEntry,
        audioBuffer: this.audioData,
      };

      this.audioCacheService.cacheWaveform(
        this.currentTrackId,
        this.audioPath,
        updatedCacheEntry,
      );

      console.log(
        '🎵 Background AudioBuffer generation complete - now ready for playback!',
      );
    } catch (error) {
      console.error('Error generating AudioBuffer in background:', error);
      // Don't update loading states here - user already sees the waveform
    }
  }

  private async generateFreshWaveform(containerWidth: number): Promise<void> {
    try {
      // Fetch audio data (this is the expensive operation)
      this.audioData = await getAudioData(this.audioPath);
      if (!this.audioData) {
        throw new Error('Failed to load audio data');
      }

      this.duration = this.audioData?.duration || 0;

      // Generate waveform immediately for faster display
      this.generateWaveform(containerWidth);
    } catch (error) {
      console.error('Error in generateFreshWaveform:', error);
      this.loading = false;
      this.isProcessingWaveform = false;
    }
  }

  private generateWaveform(containerWidth: number): void {
    if (
      this._isDestroyed ||
      !this.svgContainer?.nativeElement ||
      !this.audioData
    ) {
      this.isProcessingWaveform = false;
      this.loading = false;
      return;
    }

    // If container width is 0, get it again or use fallback
    let actualWidth = containerWidth;
    if (actualWidth <= 0) {
      actualWidth =
        this.svgContainer.nativeElement.getBoundingClientRect().width;

      // Still 0? Use a reasonable fallback
      if (actualWidth <= 0) {
        actualWidth = 800; // Fallback width
      }
    }

    this.width.set(actualWidth);

    // Generate waveform path using your existing library
    this.drawPath = linearPath(this.audioData, {
      samples: this.samples(),
      width: this.width(),
      height: this.height(),
      type: this.type(),
      top: 0,
      paths: this.paths(),
    });

    // Apply the generated path immediately for faster display
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      if (this._isDestroyed) {
        this.isProcessingWaveform = false;
        return;
      }

      // Try ViewChild first, fallback to querySelector
      let pathElement = this.svgPath?.nativeElement;
      if (!pathElement) {
        pathElement = document.querySelector(
          '#audioSvg path',
        ) as SVGPathElement;
      }

      if (pathElement) {
        pathElement.setAttribute('d', this.drawPath);
        this.lastGeneratedWidth = containerWidth;

        // Cache the generated waveform
        this.cacheWaveformData(containerWidth);

        // Complete loading and processing states
        this.loading = false;
        this.isProcessingWaveform = false;
        this.setComponentsProps();
        this.setPreviewWidth();

        // Emit audio data loaded event ONLY after everything is complete
        this.mzicAudioDataLoaded.emit({
          audioBuffer: this.audioData,
          duration: this.duration,
          trackId: this.currentTrackId,
        });
      } else {
        console.error('Could not find path element for waveform');
        this.loading = false;
        this.isProcessingWaveform = false;
      }
    });
  }

  private cacheWaveformData(width: number): void {
    if (!this.audioData || !this.drawPath || !this.currentTrackId) {
      return;
    }

    const cacheEntry = {
      audioBuffer: this.audioData,
      waveformPath: this.drawPath,
      duration: this.duration,
      lastModified: Date.now(),
      width: width,
    };

    this.audioCacheService.cacheWaveform(
      this.currentTrackId,
      this.audioPath,
      cacheEntry,
    );
  }

  private generateTrackIdFromPath(audioPath: string): string {
    // Use provided trackId if available for better cache efficiency
    if (this.trackId()) {
      return this.trackId()!;
    }

    // Fallback: Extract identifier from the audio path
    const urlParts = audioPath.split('/');
    const fileName = urlParts[urlParts.length - 1];
    return fileName.split('?')[0] || audioPath;
  }

  setPreviewWidth() {
    const previewContainer = this.svgContainer?.nativeElement?.querySelector(
      '#previewContainer',
    ) as HTMLElement;

    if (previewContainer) {
      const width =
        (this.previewDuration() * this.svgContainerWidth) / this.duration + 4; // +4 de border de 2px pra cada lado

      if (this.previewDuration() >= this.duration) {
        previewContainer.style.width = `100%`;
      } else {
        previewContainer.style.width = `${width}px`;
      }
    }
  }

  async onFileChange(event: any) {
    const file = event.target.files[0];
    if (event?.target?.files[0]) {
      this.setFile({ name: file.name, path: URL.createObjectURL(file) });
    }
  }

  play() {
    if (this.audioPlayer?.nativeElement) {
      this.audioPlayer.nativeElement.play();
      this.audioIsPlaying = true;
    }
  }

  pause() {
    if (this.audioPlayer?.nativeElement) {
      this.audioPlayer.nativeElement.pause();
      this.audioIsPlaying = false;
    }
  }

  audioError(event: any) {
    console.error('🎵 Audio element error:', event);
    console.error('🎵 Audio path:', this.audioPath);
    console.error('🎵 Audio element error details:', {
      error: event.target?.error,
      code: event.target?.error?.code,
      message: event.target?.error?.message,
      networkState: event.target?.networkState,
      readyState: event.target?.readyState,
      src: event.target?.src,
    });

    // Only emit error if we have a valid audio path set
    // Ignore errors from empty src during initialization
    if (!this._isDestroyed && this.audioPath) {
      this.loading = false;
      this.isProcessingWaveform = false;
      this.mzicError.emit(event);
    } else {
      console.log(
        '🎵 Ignoring audio error during initialization (no audioPath set)',
      );
    }
  }
  audioPlay(event: Event) {
    if (!this._isDestroyed) {
      this.mzicPlay.emit(event);
    }
  }
  audioPlaying(event: Event) {
    if (!this._isDestroyed) {
      this.mzicPlaying.emit(event);
    }
  }
  audioPause(event: Event) {
    if (!this._isDestroyed) {
      this.mzicPause.emit(event);
    }
  }
  audioTimeUpdate(event: Event, element: MzicAudioPlayerComponent) {
    if (this._isDestroyed) {
      return;
    }

    const audioData = element.audioData as AudioBuffer;
    const audioPlayer = element.audioPlayer?.nativeElement as HTMLAudioElement;

    if (audioData?.duration && audioPlayer) {
      const currentTime = audioPlayer.currentTime;
      this.audioPercent = currentTime / audioData?.duration;

      // Essa condição verifica se o commponent está habilitado para fazer o preview
      // e se o tempo atual tempo atual da música é maior que o tempo de início do preview + a duração do preview.
      // Exemplo: o usuário setou a música para começar em 10 segundos e o tempo do preview é de 15 segundos,
      // a música só tocará de 10 segundos até 25 segundos (10+15), após isso, a música precisa ser pausada para não
      // ultrapassar o tempo de preview máximo.
      if (
        this.showPreview &&
        currentTime > this.startPlayTimeInsidePreview + this.previewDuration()
      ) {
        this.ctrl()?.next({ action: 'pause' });
      }
    }

    this.mzicTimeUpdate.emit(event);
  }

  audioCanplay(event: Event) {
    if (!this._isDestroyed) {
      this.mzicCanPlay.emit(event);
    }
  }

  audioLoadedMetadata(event: Event) {
    if (!this._isDestroyed) {
      this.mzicLoadStart.emit(event);
    }
  }

  audioLoadstart(event: Event) {
    if (!this._isDestroyed) {
      this.mzicLoadStart.emit(event);
    }
  }

  audioEnded(event: Event) {
    if (!this._isDestroyed) {
      this.mzicEnded.emit(event);
      this.audioIsPlaying = false;
    }
  }

  audioLoadedData(event: Event) {
    if (!this._isDestroyed) {
      this.mzicLoadedData.emit(event);
    }
  }
}
